import { Link } from "react-router-dom";
import { ReactNode } from "react";

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface HeroSectionProps {
  variant?: "full" | "half";
  bgImage?: string;
  bgImageAlt?: string;
  badge?: string;
  h1?: ReactNode;
  subtitle?: string;
  breadcrumb?: BreadcrumbItem[];
  showDualCTA?: boolean;
  showTrustSignals?: boolean;
  accentLine?: string;
}

export default function HeroSection({
  variant = "full",
  bgImage,
  bgImageAlt,
  badge,
  h1,
  subtitle,
  breadcrumb,
  showDualCTA = true,
  showTrustSignals = false,
  accentLine,
}: HeroSectionProps) {
  const isHalf = variant === "half";
  const heightClass = isHalf ? "min-h-[72vh]" : "min-h-screen";

  const defaultBg =
    "https://public.readdy.ai/ai/img_res/9754afb10a563950e88016fb8f2c50d6.jpg";

  return (
    <section className={`relative ${heightClass} flex flex-col justify-center overflow-hidden`}>
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={bgImage ?? defaultBg}
          alt={
            bgImageAlt ??
            "Westec Builders premium custom construction Colorado Springs"
          }
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className={`relative z-10 max-w-7xl mx-auto px-6 w-full ${isHalf ? "pt-28 pb-16" : "pt-36 pb-20"}`}>
        {/* Breadcrumb */}
        {breadcrumb && breadcrumb.length > 0 && (
          <nav className="flex items-center gap-2 text-xs font-body text-white/60 mb-6">
            {breadcrumb.map((crumb, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && (
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line" />
                  </span>
                )}
                {crumb.to ? (
                  <Link to={crumb.to} className="hover:text-white transition-colors cursor-pointer">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-white/90">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-shield-check-fill text-brand-amber text-sm" />
          </span>
          <span className="text-white/90 text-xs font-body font-medium tracking-wide">
            {badge ?? "Licensed & Insured • Colorado Springs, CO"}
          </span>
        </div>

        <div className="max-w-3xl">
          {/* H1 */}
          <h1
            className={`font-heading font-bold text-white leading-[1.05] mb-6 ${
              isHalf ? "text-5xl lg:text-6xl" : "text-6xl lg:text-8xl"
            }`}
          >
            {h1 ?? (
              <>
                Premium Custom<br />
                Construction &amp;<br />
                <span className="text-brand-amber">{accentLine ?? "Remodeling"}</span><br />
                Built to Last
              </>
            )}
          </h1>

          {/* Subtitle */}
          <p className="font-body text-white/80 text-lg leading-relaxed max-w-xl mb-10">
            {subtitle ??
              "Westec Builders delivers high-quality residential and commercial construction, remodeling, and renovation solutions with precision craftsmanship and reliable project management across Colorado Springs."}
          </p>

          {/* CTAs */}
          {showDualCTA && (
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/#estimate"
                className="bg-brand-amber text-white px-8 py-4 rounded-md font-body font-bold text-sm hover:bg-brand-amber-light transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Get a Free Estimate
              </Link>
              <a
                href="tel:+17195550100"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/50 text-white px-8 py-4 rounded-md font-body font-bold text-sm hover:bg-white/10 hover:border-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-sm" />
                </span>
                Call Now — (719) 555-0100
              </a>
            </div>
          )}

          {/* Trust signals */}
          {showTrustSignals && (
            <div className="flex flex-wrap gap-5">
              {[
                "Licensed & Insured",
                "15+ Years Experience",
                "500+ Projects Completed",
                "98% On-Time Delivery",
              ].map((item) => (
                <span
                  key={item}
                  className="text-white/80 text-sm font-body flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-brand-amber text-sm" />
                  </span>
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator — only on full hero */}
      {!isHalf && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-line text-white/40 text-lg" />
          </span>
        </div>
      )}
    </section>
  );
}
